import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
let cached = esc(require('@glimmer/tracking')).cached;
import { concat } from "@ember/helper";
import { on } from "@ember/modifier";
import { action } from "@ember/object";
import didInsert from "@ember/render-modifiers/modifiers/did-insert";
import willDestroy from "@ember/render-modifiers/modifiers/will-destroy";
import { service } from "@ember/service";
import { modifier as modifierFn } from "ember-modifier";
import { and, not, or } from "truth-helpers";
import ConditionalInElement from "discourse/components/conditional-in-element";
import DButton from "discourse/components/d-button";
import concatClass from "discourse/helpers/concat-class";
import element from "discourse/helpers/element";
import { disableBodyScroll, enableBodyScroll } from "discourse/lib/body-scroll-lock";
import { getMaxAnimationTimeMs } from "discourse/lib/swipe-events";
import swipe from "discourse/modifiers/swipe";
import trapTab from "discourse/modifiers/trap-tab";
import { bind } from "discourse-common/utils/decorators";
import { setComponentTemplate } from "@ember/component";
import { createTemplateFactory } from "@ember/template-factory";
import esc from "../../../../../../../../node_modules/@embroider/macros/src/addon/es-compat2";
export const CLOSE_INITIATED_BY_BUTTON = "initiatedByCloseButton";
export const CLOSE_INITIATED_BY_ESC = "initiatedByESC";
export const CLOSE_INITIATED_BY_CLICK_OUTSIDE = "initiatedByClickOut";
export const CLOSE_INITIATED_BY_MODAL_SHOW = "initiatedByModalShow";
export const CLOSE_INITIATED_BY_SWIPE_DOWN = "initiatedBySwipeDown";
const FLASH_TYPES = ["success", "error", "warning", "info"];
const SWIPE_VELOCITY_THRESHOLD = 0.4;
export default class DModal extends Component {
  static #_ = (() => dt7948.g(this.prototype, "modal", [service]))();
  #modal = (() => (dt7948.i(this, "modal"), void 0))();
  static #_2 = (() => dt7948.g(this.prototype, "site", [service]))();
  #site = (() => (dt7948.i(this, "site"), void 0))();
  static #_3 = (() => dt7948.g(this.prototype, "appEvents", [service]))();
  #appEvents = (() => (dt7948.i(this, "appEvents"), void 0))();
  static #_4 = (() => dt7948.g(this.prototype, "wrapperElement", [tracked]))();
  #wrapperElement = (() => (dt7948.i(this, "wrapperElement"), void 0))();
  static #_5 = (() => dt7948.g(this.prototype, "animating", [tracked], function () {
    return false;
  }))();
  #animating = (() => (dt7948.i(this, "animating"), void 0))();
  registerModalContainer = (() => modifierFn(el1 => {
    this.modalContainer = el1;
  }))();
  setupModalBody = (() => modifierFn(el1 => {
    if (!this.site.mobileView) {
      return;
    }
    disableBodyScroll(el1);
    return () => {
      enableBodyScroll(el1);
    };
  }))();
  async setupModal(el1) {
    document.documentElement.addEventListener("keydown", this.handleDocumentKeydown);
    this.appEvents.on("keyboard-visibility-change", this.handleKeyboardVisibilityChange);
    if (this.site.mobileView) {
      this.animating = true;
      await el1.animate([{
        transform: "translateY(100%)"
      }, {
        transform: "translateY(0)"
      }], {
        duration: getMaxAnimationTimeMs(),
        easing: "ease",
        fill: "forwards"
      }).finished;
      this.animating = false;
    }
    this.wrapperElement = el1;
  }
  static #_6 = (() => dt7948.n(this.prototype, "setupModal", [action]))();
  cleanupModal() {
    document.documentElement.removeEventListener("keydown", this.handleDocumentKeydown);
    this.appEvents.off("keyboard-visibility-change", this.handleKeyboardVisibilityChange);
  }
  static #_7 = (() => dt7948.n(this.prototype, "cleanupModal", [action]))();
  get dismissable() {
    if (!this.args.closeModal) {
      return false;
    } else if ("dismissable" in this.args) {
      return this.args.dismissable;
    } else {
      return true;
    }
  }
  shouldTriggerClickOnEnter(event1) {
    if (this.args.submitOnEnter === false) {
      return false;
    }
    // skip when in a form or a textarea element
    if (event1.target.closest("form") || document.activeElement?.nodeName === "TEXTAREA") {
      return false;
    }
    return true;
  }
  async handleSwipe(swipeEvent1) {
    if (this.animating) {
      return;
    }
    if (swipeEvent1.deltaY >= 0) {
      return await this.#animateWrapperPosition(swipeEvent1.deltaY);
    }
  }
  static #_8 = (() => dt7948.n(this.prototype, "handleSwipe", [action]))();
  async handleSwipeEnded(swipeEvent1) {
    if (this.animating) {
      // if the modal is animating we don't want to risk resetting the position
      // as the user releases the swipe at the same time
      return;
    }
    if (swipeEvent1.goingUp() || swipeEvent1.velocityY < SWIPE_VELOCITY_THRESHOLD) {
      return await this.#animateWrapperPosition(0);
    }
    this.modalContainer.style.transform = `translateY(${swipeEvent1.deltaY}px)`;
    this.closeModal(CLOSE_INITIATED_BY_SWIPE_DOWN);
  }
  static #_9 = (() => dt7948.n(this.prototype, "handleSwipeEnded", [action]))();
  handleWrapperClick(e1) {
    if (e1.button !== 0) {
      return; // Non-default mouse button
    }
    if (!this.dismissable) {
      return;
    }
    return this.closeModal(CLOSE_INITIATED_BY_CLICK_OUTSIDE);
  }
  static #_10 = (() => dt7948.n(this.prototype, "handleWrapperClick", [action]))();
  async closeModal(initiatedBy1) {
    if (!this.args.closeModal) {
      return;
    }
    if (this.site.mobileView) {
      this.animating = true;
      this.#animateBackdropOpacity(window.innerHeight);
      await this.#animateWrapperPosition(this.modalContainer.clientHeight);
      this.animating = false;
    }
    this.args.closeModal({
      initiatedBy: initiatedBy1
    });
  }
  static #_11 = (() => dt7948.n(this.prototype, "closeModal", [action]))();
  handleDocumentKeydown(event1) {
    if (this.args.hidden) {
      return;
    }
    if (event1.key === "Escape" && this.dismissable) {
      event1.stopPropagation();
      this.closeModal(CLOSE_INITIATED_BY_ESC);
    }
    if (event1.key === "Enter" && this.shouldTriggerClickOnEnter(event1)) {
      this.wrapperElement.querySelector(".d-modal__footer .btn-primary")?.click();
      event1.preventDefault();
    }
  }
  static #_12 = (() => dt7948.n(this.prototype, "handleDocumentKeydown", [action]))();
  handleCloseButton() {
    this.closeModal(CLOSE_INITIATED_BY_BUTTON);
  }
  static #_13 = (() => dt7948.n(this.prototype, "handleCloseButton", [action]))();
  validateFlashType(type1) {
    if (type1 && !FLASH_TYPES.includes(type1)) {
      throw `@flashType must be one of ${FLASH_TYPES.join(", ")}`;
    }
  }
  // Could be optimised to remove classic component once RFC389 is implemented
  // https://rfcs.emberjs.com/id/0389-dynamic-tag-names
  static #_14 = (() => dt7948.n(this.prototype, "validateFlashType", [action]))();
  get dynamicElement() {
    const tagName1 = this.args.tagName || "div";
    if (!["div", "form"].includes(tagName1)) {
      throw `@tagName must be form or div`;
    }
    return element(tagName1);
  }
  static #_15 = (() => dt7948.n(this.prototype, "dynamicElement", [cached]))();
  handleKeyboardVisibilityChange(visible1) {
    if (visible1) {
      window.scrollTo(0, 0);
    }
  }
  static #_16 = (() => dt7948.n(this.prototype, "handleKeyboardVisibilityChange", [bind]))();
  #animateBackdropOpacity(position1) {
    const backdrop1 = this.wrapperElement.nextElementSibling;
    if (!backdrop1) {
      return;
    }
    const opacity1 = 1 - position1 / this.modalContainer.clientHeight;
    backdrop1.animate([{
      opacity: Math.max(0, Math.min(opacity1, 0.6))
    }], {
      fill: "forwards"
    });
  }
  async #animateWrapperPosition(position1) {
    this.#animateBackdropOpacity(position1);
    await this.modalContainer.animate([{
      transform: `translateY(${position1}px)`
    }], {
      fill: "forwards",
      duration: getMaxAnimationTimeMs()
    }).finished;
  }
  static #_17 = (() => setComponentTemplate(createTemplateFactory(
  /*
    
      {{! template-lint-disable no-invalid-interactive }}
  
      <ConditionalInElement
        @element={{this.modal.containerElement}}
        @inline={{@inline}}
        @append={{true}}
      >
        <this.dynamicElement
          class={{concatClass
            "modal"
            "d-modal"
            (if @inline "-inline")
            (if this.animating "is-animating")
          }}
          data-keyboard="false"
          aria-modal="true"
          role="dialog"
          aria-labelledby={{if @title "discourse-modal-title"}}
          ...attributes
          {{didInsert this.setupModal}}
          {{willDestroy this.cleanupModal}}
          {{trapTab preventScroll=false}}
        >
          <div class="d-modal__container" {{this.registerModalContainer}}>
            {{yield to="aboveHeader"}}
  
            {{#if
              (and
                (not @hideHeader)
                (or
                  this.dismissable
                  @title
                  (has-block "headerBelowTitle")
                  (has-block "headerAboveTitle")
                )
              )
            }}
              <div
                class={{concatClass "d-modal__header" @headerClass}}
                {{swipe
                  onDidSwipe=this.handleSwipe
                  onDidEndSwipe=this.handleSwipeEnded
                  enabled=this.dismissable
                }}
              >
                {{yield to="headerAboveTitle"}}
  
                {{#if
                  (and
                    this.site.mobileView
                    this.dismissable
                    (has-block "headerPrimaryAction")
                  )
                }}
                  <div class="d-modal__dismiss-action">
                    <DButton
                      @label="cancel"
                      @action={{this.handleCloseButton}}
                      @title="modal.close"
                      class="btn-transparent btn-primary d-modal__dismiss-action-button"
                    />
                  </div>
                {{/if}}
  
                {{#if @title}}
                  <div class="d-modal__title">
                    <h1
                      id="discourse-modal-title"
                      class="d-modal__title-text"
                    >{{@title}}</h1>
  
                    {{#if @subtitle}}
                      <p class="d-modal__subtitle-text">{{@subtitle}}</p>
                    {{/if}}
  
                    {{yield to="belowModalTitle"}}
                  </div>
                {{/if}}
                {{yield to="headerBelowTitle"}}
  
                {{#if
                  (and this.site.mobileView (has-block "headerPrimaryAction"))
                }}
                  <div class="d-modal__primary-action">
                    {{yield to="headerPrimaryAction"}}
                  </div>
                {{else if this.dismissable}}
                  <DButton
                    @icon="times"
                    @action={{this.handleCloseButton}}
                    @title="modal.close"
                    class="btn-transparent modal-close"
                  />
                {{/if}}
              </div>
            {{/if}}
  
            {{yield to="belowHeader"}}
  
            {{this.validateFlashType @flashType}}
            {{#if @flash}}
              <div
                id="modal-alert"
                role="alert"
                class={{concatClass
                  "alert"
                  (if @flashType (concat "alert-" @flashType))
                }}
              >
                {{~@flash~}}
              </div>
            {{/if}}
  
            <div
              class={{concatClass "d-modal__body" @bodyClass}}
              {{this.setupModalBody}}
              tabindex="-1"
            >
              {{#if (has-block "body")}}
                {{yield to="body"}}
              {{else}}
                {{yield}}
              {{/if}}
            </div>
  
            {{#if (has-block "footer")}}
              <div class="d-modal__footer">
                {{yield to="footer"}}
              </div>
            {{/if}}
  
            {{yield to="belowFooter"}}
          </div>
        </this.dynamicElement>
        {{#unless @inline}}
          <div
            class="d-modal__backdrop"
            {{swipe
              onDidSwipe=this.handleSwipe
              onDidEndSwipe=this.handleSwipeEnded
              enabled=this.dismissable
            }}
            {{on "click" this.handleWrapperClick}}
          ></div>
        {{/unless}}
      </ConditionalInElement>
    
  */
  {
    "id": "UrLgteKj",
    "block": "[[[1,\"\\n\"],[1,\"\\n    \"],[8,[32,0],null,[[\"@element\",\"@inline\",\"@append\"],[[30,0,[\"modal\",\"containerElement\"]],[30,1],true]],[[\"default\"],[[[[1,\"\\n      \"],[8,[30,0,[\"dynamicElement\"]],[[16,0,[28,[32,1],[\"modal\",\"d-modal\",[52,[30,1],\"-inline\"],[52,[30,0,[\"animating\"]],\"is-animating\"]],null]],[24,\"data-keyboard\",\"false\"],[24,\"aria-modal\",\"true\"],[24,\"role\",\"dialog\"],[16,\"aria-labelledby\",[52,[30,2],\"discourse-modal-title\"]],[17,3],[4,[32,2],[[30,0,[\"setupModal\"]]],null],[4,[32,3],[[30,0,[\"cleanupModal\"]]],null],[4,[32,4],null,[[\"preventScroll\"],[false]]]],null,[[\"default\"],[[[[1,\"\\n        \"],[11,0],[24,0,\"d-modal__container\"],[4,[30,0,[\"registerModalContainer\"]],null,null],[12],[1,\"\\n          \"],[18,10,null],[1,\"\\n\\n\"],[41,[28,[32,5],[[28,[32,6],[[30,4]],null],[28,[32,7],[[30,0,[\"dismissable\"]],[30,2],[48,[30,11]],[48,[30,12]]],null]],null],[[[1,\"            \"],[11,0],[16,0,[28,[32,1],[\"d-modal__header\",[30,5]],null]],[4,[32,8],null,[[\"onDidSwipe\",\"onDidEndSwipe\",\"enabled\"],[[30,0,[\"handleSwipe\"]],[30,0,[\"handleSwipeEnded\"]],[30,0,[\"dismissable\"]]]]],[12],[1,\"\\n              \"],[18,12,null],[1,\"\\n\\n\"],[41,[28,[32,5],[[30,0,[\"site\",\"mobileView\"]],[30,0,[\"dismissable\"]],[48,[30,13]]],null],[[[1,\"                \"],[10,0],[14,0,\"d-modal__dismiss-action\"],[12],[1,\"\\n                  \"],[8,[32,9],[[24,0,\"btn-transparent btn-primary d-modal__dismiss-action-button\"]],[[\"@label\",\"@action\",\"@title\"],[\"cancel\",[30,0,[\"handleCloseButton\"]],\"modal.close\"]],null],[1,\"\\n                \"],[13],[1,\"\\n\"]],[]],null],[1,\"\\n\"],[41,[30,2],[[[1,\"                \"],[10,0],[14,0,\"d-modal__title\"],[12],[1,\"\\n                  \"],[10,\"h1\"],[14,1,\"discourse-modal-title\"],[14,0,\"d-modal__title-text\"],[12],[1,[30,2]],[13],[1,\"\\n\\n\"],[41,[30,6],[[[1,\"                    \"],[10,2],[14,0,\"d-modal__subtitle-text\"],[12],[1,[30,6]],[13],[1,\"\\n\"]],[]],null],[1,\"\\n                  \"],[18,14,null],[1,\"\\n                \"],[13],[1,\"\\n\"]],[]],null],[1,\"              \"],[18,11,null],[1,\"\\n\\n\"],[41,[28,[32,5],[[30,0,[\"site\",\"mobileView\"]],[48,[30,13]]],null],[[[1,\"                \"],[10,0],[14,0,\"d-modal__primary-action\"],[12],[1,\"\\n                  \"],[18,13,null],[1,\"\\n                \"],[13],[1,\"\\n\"]],[]],[[[41,[30,0,[\"dismissable\"]],[[[1,\"                \"],[8,[32,9],[[24,0,\"btn-transparent modal-close\"]],[[\"@icon\",\"@action\",\"@title\"],[\"times\",[30,0,[\"handleCloseButton\"]],\"modal.close\"]],null],[1,\"\\n              \"]],[]],null]],[]]],[1,\"            \"],[13],[1,\"\\n\"]],[]],null],[1,\"\\n          \"],[18,15,null],[1,\"\\n\\n          \"],[1,[28,[30,0,[\"validateFlashType\"]],[[30,7]],null]],[1,\"\\n\"],[41,[30,8],[[[1,\"            \"],[10,0],[14,1,\"modal-alert\"],[14,\"role\",\"alert\"],[15,0,[28,[32,1],[\"alert\",[52,[30,7],[28,[32,10],[\"alert-\",[30,7]],null]]],null]],[12],[1,[30,8]],[13],[1,\"\\n\"]],[]],null],[1,\"\\n          \"],[11,0],[16,0,[28,[32,1],[\"d-modal__body\",[30,9]],null]],[24,\"tabindex\",\"-1\"],[4,[30,0,[\"setupModalBody\"]],null,null],[12],[1,\"\\n\"],[41,[48,[30,16]],[[[1,\"              \"],[18,16,null],[1,\"\\n\"]],[]],[[[1,\"              \"],[18,17,null],[1,\"\\n\"]],[]]],[1,\"          \"],[13],[1,\"\\n\\n\"],[41,[48,[30,18]],[[[1,\"            \"],[10,0],[14,0,\"d-modal__footer\"],[12],[1,\"\\n              \"],[18,18,null],[1,\"\\n            \"],[13],[1,\"\\n\"]],[]],null],[1,\"\\n          \"],[18,19,null],[1,\"\\n        \"],[13],[1,\"\\n      \"]],[]]]]],[1,\"\\n\"],[41,[51,[30,1]],[[[1,\"        \"],[11,0],[24,0,\"d-modal__backdrop\"],[4,[32,8],null,[[\"onDidSwipe\",\"onDidEndSwipe\",\"enabled\"],[[30,0,[\"handleSwipe\"]],[30,0,[\"handleSwipeEnded\"]],[30,0,[\"dismissable\"]]]]],[4,[32,11],[\"click\",[30,0,[\"handleWrapperClick\"]]],null],[12],[13],[1,\"\\n\"]],[]],null],[1,\"    \"]],[]]]]],[1,\"\\n  \"]],[\"@inline\",\"@title\",\"&attrs\",\"@hideHeader\",\"@headerClass\",\"@subtitle\",\"@flashType\",\"@flash\",\"@bodyClass\",\"&aboveHeader\",\"&headerBelowTitle\",\"&headerAboveTitle\",\"&headerPrimaryAction\",\"&belowModalTitle\",\"&belowHeader\",\"&body\",\"&default\",\"&footer\",\"&belowFooter\"],false,[\"if\",\"yield\",\"has-block\",\"unless\"]]",
    "moduleName": "/var/www/discourse/app/assets/javascripts/discourse/node_modules/.embroider/rewritten-app/components/d-modal.js",
    "scope": () => [ConditionalInElement, concatClass, didInsert, willDestroy, trapTab, and, not, or, swipe, DButton, concat, on],
    "isStrictMode": true
  }), this))();
}